import Component from "../../lib/component";
import { identifyElement } from "../../helpers/element";
import DOMUpdateEvent from "../../helpers/dom_update_event";
import { fireElementEvent } from "../../helpers/event";
import { buildOptionControl } from "./dropdown";

export const DropdownNavConfig = {
  componentTypeKey: "DropdownNav",
  baseHtmlClass: "dropdown_nav",
  selector: ".dropdown_nav",

  datasetConfigKey: "dropdown_nav__config",
};

export class DropdownNav extends Component {

    static get config() {
        return DropdownNavConfig;
    }

    constructor(id_or_element, dropdown) {
        super({
            element: id_or_element,
            config: DropdownNav.config
        });

        this.dropdown = dropdown;

        this.setDatasetConfig(DropdownNav.config.datasetConfigKey);

        this.listElement = this.element.querySelector('ul');

        // Select mode: activating a select item updates the trigger label (opt-in).
        this.updateTriggerLabelOnSelect = !!this.getDatasetConfigValue('update_trigger_label_on_select');

        // One delegated listener handles activation of SELECT items (rendered as <button>).
        // LINK items (<a href>) are not intercepted — they navigate normally. Own-element
        // listener, so no onDestroy needed.
        if (this.listElement) {
            this.listElement.addEventListener('click', this.handleItemActivate.bind(this));
        }

        // State: the items to render. Each entry is one of:
        //   { kind: 'item', label, anchor }    — a navigable section link (<a>)
        //   { kind: 'select', label, value }   — a select action (<button>); fires selectedEventName
        //   { kind: 'group_title', label }     — a group heading row
        // State changes call `renderItems()`. Open/close is handled by
        // `render()` which doesn't touch items (preserves scroll position).
        this.items = [];

        // If there is "In Page" configuration then the nav scans the page for
        // matching sections and renders them dynamically.
        this.inPageConfig = this.getDatasetConfigValue('in_page_config');
        this.isInPageNav = !!(this.inPageConfig);
        if (this.isInPageNav) {
            this.inPageSectionSelector    = this.inPageConfig['section_selector']     || 'section';
            this.inPageTitleSelector      = this.inPageConfig['title_selector']       || "section > h2, section > h3";
            this.inPageGroupSelector      = this.inPageConfig['group_selector']       || null;
            this.inPageGroupTitleSelector = this.inPageConfig['group_title_selector'] || null;

            this._scanInPageNav();

            // Re-scan when dynamic content lands (e.g. a LazySnippet swap brings
            // new sections). `fireOnDOMContentLoaded: false` keeps subscribing
            // from registering an extra page-load listener.
            new DOMUpdateEvent({fireOnDOMContentLoaded: false})
                .listen(() => this._scanInPageNav());
        }

        this.render();
    }

    render() {
        this.swapHtmlClassStates(this.isOpen(), 'open', 'closed');
        this.moveIntoViewport();
    }

    moveIntoViewport() {
        const rect = this.element.getBoundingClientRect();
        const rightOverflow = rect.right - window.innerWidth;
        this.element.style.transform = (rightOverflow > 0) ? `translateX(-${rightOverflow}px)` : null;
    }

    isOpen() {
        return this.dropdown.isOpen();
    }

    /**
     * Replace the items state and re-render. Public API for callers that want
     * to push items directly (rather than via the in-page scan).
     * @param {Array<{kind: string, label: string, anchor?: string, value?: string}>} items
     */
    setItems(items) {
        this.items = items;
        this.renderItems();
    }

    // Activate a SELECT item (a <button data-value>): fire the dropdown's selectedEventName on
    // its element (name from Config, never a literal), optionally update the trigger label, close.
    // LINK items (<a>) fall through (button match fails) and navigate normally.
    handleItemActivate(event) {
        const button = event.target.closest('.dropdown_option button');
        if (!button || !this.listElement.contains(button)) return;
        event.preventDefault();
        const label = button.textContent.trim();
        fireElementEvent(this.dropdown.element, this.dropdown.config.selectedEventName, {
            value: button.dataset.value,
            label: label,
        });
        if (this.updateTriggerLabelOnSelect) this.dropdown.setTriggerLabel(label);
        this.dropdown.close();
        this.dropdown.render();
    }

    // Re-scans the page for sections (respecting grouping config if set), sets
    // `this.items`, and re-renders.
    _scanInPageNav() {
        if (!this.isInPageNav) return;
        this.items = buildItemsFromScan({
            sectionSelector:    this.inPageSectionSelector,
            titleSelector:      this.inPageTitleSelector,
            groupSelector:      this.inPageGroupSelector,
            groupTitleSelector: this.inPageGroupTitleSelector,
        });
        this.renderItems();
    }

    // Rebuild the dynamic `<li>` rows in `this.listElement` from `this.items`.
    // Preserves any non-item children (like `.dropdown_title`).
    renderItems() {
        if (!this.listElement) return;
        this.listElement.querySelectorAll('.dropdown_nav__item, .dropdown_nav__group_title').forEach((n) => n.remove());
        this.items.forEach((entry) => {
            const li = (entry.kind === 'group_title')
                ? createGroupTitleLi(entry.label)
                : createItemLi(entry);
            this.listElement.appendChild(li);
        });
    }
}

// Build the items array from a page scan. Ungrouped sections are rendered
// first (in DOM order), then each group's title row followed by its sections
// (groups in the order they first appear in the DOM).
function buildItemsFromScan({sectionSelector, titleSelector, groupSelector, groupTitleSelector}) {
    const sections = document.querySelectorAll(sectionSelector);
    const ungrouped = [];
    const groupOrder = [];          // ordered list of unique group elements
    const groupItems = new Map();   // group element → items array

    sections.forEach((section) => {
        identifyElement(section);
        const titleEl = section.querySelector(titleSelector);
        if (!titleEl) return;
        const titleText = extractHeadingText(titleEl);
        if (!titleText) return;
        const item = {kind: 'item', label: titleText, anchor: '#' + section.id};

        const group = groupSelector ? section.closest(groupSelector) : null;
        if (group) {
            if (!groupItems.has(group)) {
                groupOrder.push(group);
                groupItems.set(group, []);
            }
            groupItems.get(group).push(item);
        } else {
            ungrouped.push(item);
        }
    });

    const items = [...ungrouped];
    for (const group of groupOrder) {
        if (groupTitleSelector) {
            const groupTitleEl = group.querySelector(groupTitleSelector);
            if (groupTitleEl) {
                const groupTitleText = extractHeadingText(groupTitleEl);
                if (groupTitleText) items.push({kind: 'group_title', label: groupTitleText});
            }
        }
        items.push(...groupItems.get(group));
    }
    return items;
}

// Extract the visible heading text from an element, stripping popover triggers,
// SVG content (whose `<style>` blocks otherwise pollute textContent with raw
// CSS), and `.hide` help blocks that may appear inside the heading. Any
// whitespace run (from multi-line ERB indentation) is collapsed to a single
// space so the menu reads cleanly.
function extractHeadingText(element) {
    const clone = element.cloneNode(true);
    clone.querySelectorAll('.popover, svg, .hide').forEach((n) => n.remove());
    return clone.textContent.replace(/\s+/g, ' ').trim();
}

// Build one item row from an entry. The control (link <a> or select <button>) is built by the
// shared `buildOptionControl` (imported from ./dropdown) so the static-items path and this
// in-page-scan path stay in lockstep.
function createItemLi(entry) {
    const li = document.createElement('li');
    li.classList.add('dropdown_nav__item');
    const option = document.createElement('div');
    option.classList.add('dropdown_option');
    option.appendChild(buildOptionControl(entry));
    li.appendChild(option);
    return li;
}

function createGroupTitleLi(label) {
    const li = document.createElement('li');
    li.classList.add('dropdown_nav__group_title');
    const span = document.createElement('span');
    span.textContent = label;
    li.appendChild(span);
    return li;
}
